// The project layer's shared leaf (owner rulings of 2026-10-02, escalation 20261002-144639; ADR 0017): the few pieces
// the server's validator and stores, the offline summariser and the client's pre-checks must agree on. Shared so
// each side computes the same key, the same colour slot and the same "is this a person's name" answer.
import { prefixUnits, stableHash } from './text.js';

/** A project name's bound (`projects.name` and `name_key` CHECKs; owner ruling 4/5: each name "bounded in length"). */
export const PROJECT_NAME_MAX = 60;

/**
 * The design system's label family has eight slots (`--label-1 … --label-8` with their foregrounds, 01-foundations
 * §1.8; owner ruling 15: "a colour per project from day one").
 */
export const PROJECT_LABEL_SLOTS = 8;

/**
 * How a name finds its project row (`projects.name_key`): the `todoDedupeKey` recipe — NFKC, lower-case, every run of
 * non-alphanumerics collapsed to one space, trimmed — so a name the model repeats with other punctuation or case maps
 * to the one row. Why copied rather than imported: `todoDedupeKey` lives in the server's to-do store
 * (src/server/stores/todos.ts), and the client pre-checks a name before asking. Why cut: NFKC can lengthen a name
 * (a ligature becomes two letters), and the `name_key` CHECK bounds the key as the `name` CHECK bounds the name, so
 * the key is cut to PROJECT_NAME_MAX units — never through a surrogate pair — and trimmed again.
 */
export function projectNameKey(name: string): string {
  const key = name.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  return key.length > PROJECT_NAME_MAX ? prefixUnits(key, PROJECT_NAME_MAX).trimEnd() : key;
}

/**
 * The 1-based label slot for a project's id: a stable hash of the id mod eight (owner ruling 15), so a rename keeps its
 * colour and the same project is the same colour on every screen. Colour is never the only signal.
 */
export function projectLabelSlot(id: string): number {
  return (stableHash(id) % PROJECT_LABEL_SLOTS) + 1;
}

/**
 * Words that join names rather than name anything: dropped from a candidate before its tokens are compared. `&`, `/`,
 * `+` and `,` are not listed because keying already turns every non-alphanumeric run into a space.
 */
const CONNECTOR_TOKENS: ReadonlySet<string> = new Set(['and', 'with']);

/**
 * Whether a candidate project name is really a person's name — the owner's or an attendee's — so the validator and the
 * offline summariser alike refuse it (`names_a_person`). Token-level: after keying both sides and dropping the
 * connectors, true when the whole key equals a person's key, or when every remaining token of the candidate is a token
 * of some person's name — so "Nino & Giorgi" names people when the attendees are Nino Beridze and Giorgi Kapanadze,
 * while "Alpine Logistics" does not. A candidate with no tokens left (blank, or connectors alone) is not a person.
 */
export function namesAPerson(candidate: string, people: readonly string[]): boolean {
  const keys = people.map(projectNameKey).filter((key) => key.length > 0);
  if (keys.length === 0) return false;
  const key = projectNameKey(candidate);
  if (key.length === 0) return false;
  if (keys.includes(key)) return true;
  const tokens = key.split(' ').filter((token) => !CONNECTOR_TOKENS.has(token));
  if (tokens.length === 0) return false;
  const personTokens = new Set(keys.flatMap((personKey) => personKey.split(' ')));
  return tokens.every((token) => personTokens.has(token));
}

/**
 * A known project's handle as the fence sends it to the model (`PJ1 … PJ9999`). Why `PJ` and not `P`: the Slack check
 * and the sweep already use `P#` for the previous check or sweep, and a handle must never collide with one the prompt
 * already uses; every prompt's neutraliser must learn it, or a `[PJ3]` typed into a message forges a reference.
 */
export const PROJECT_HANDLE = /^PJ\d{1,4}$/u;

/** A new project's handle, as the model names one it proposes within one answer (`NP1 … NP999`). */
export const NEW_PROJECT_HANDLE = /^NP\d{1,3}$/u;
